import {
  type CaraWorkspaceAmsContextInput,
  type CaraWorkspaceAmsRefInput,
  IntegrationType,
  KnowledgeGraphEntityType
} from '~/api/graphql';
import {
  type AmsContextPinSelection,
  type AmsDocumentSelection,
  type AmsPickerConfirmResult
} from '~/components/ams-picker';
import { type AmsRef, amsRefInput } from '~/components/ams-picker/ams-ref';

/**
 * A single attachment chip displayed alongside the Cara compose input. The
 * `kind` tag selects how the chip renders and which payload gets threaded
 * through on submit.
 *
 * Today only `file` is in use, but the surrounding hooks/components are
 * structured around this union so additional attachment kinds (custom forms,
 * Word docs, HTML templates, integration files, etc.) can be slotted in
 * without restructuring the compose UI.
 */
export type CaraAttachment =
  | CaraFileAttachment
  | CaraCustomFormAttachment
  | CaraWordTemplateAttachment
  | CaraExcelTemplateAttachment
  | CaraHtmlTemplateAttachment
  | CaraAmsDocumentAttachment
  | CaraAmsContextPinAttachment;

/** A locally-uploaded file that will be sent as a multipart upload. */
export type CaraFileAttachment = {
  kind: 'file';
  /** Stable id for React keys; `File` itself isn't unique (same name twice). */
  id: string;
  file: File;
};

export type CaraCustomFormAttachment = {
  kind: 'custom-form';
  /** Custom-form id from the library; also the chip's React key. */
  id: string;
  title: string;
};

export type CaraWordTemplateAttachment = {
  kind: 'word-template';
  id: string;
  title: string;
};

export type CaraExcelTemplateAttachment = {
  kind: 'excel-template';
  id: string;
  title: string;
};

export type CaraHtmlTemplateAttachment = {
  kind: 'html-template';
  id: string;
  title: string;
};

/** A document picked from a connected AMS (Applied Epic, AMS360, NowCerts, ...). */
export type CaraAmsDocumentAttachment = {
  kind: 'ams-document';
  /** KnowledgeGraphEntity ID of the document, or the key of its `ref`. */
  id: string;
  integrationId: string;
  integrationType: IntegrationType;
  displayName: string;
  accountId: string;
  accountLabel?: string;
  /** Set for a pick from the AMS browser, which is sent as an amsRefs pin. */
  ref?: AmsRef;
};

/** A structured AMS record (policy line, certificate, …) pinned via amsContext. */
export type CaraAmsContextPinAttachment = {
  kind: 'ams-context-pin';
  id: string;
  entityType: KnowledgeGraphEntityType;
  integrationId: string;
  integrationType: IntegrationType;
  displayName: string;
  accountId: string;
  accountLabel?: string;
  /** Set for a pick from the AMS browser, which is sent as an amsRefs pin. */
  ref?: AmsRef;
};

export type CaraAttachmentKind = CaraAttachment['kind'];

/** Attachment kinds that are library picks carrying an `{id, title}` shape. */
export type CaraTemplateAttachmentKind = Exclude<
  CaraAttachmentKind,
  'file' | 'ams-document' | 'ams-context-pin'
>;

/**
 * Pulls the `File` payload out of every `file`-kind attachment, preserving
 * order. Convenient at the submit boundary where the parent expects
 * `File[]` (see `CaraInput.onSubmit`).
 */
export const filesFromAttachments = (attachments: CaraAttachment[]): File[] =>
  attachments.filter((a): a is CaraFileAttachment => a.kind === 'file').map((a) => a.file);

export const amsContextFromAttachments = (
  attachments: CaraAttachment[]
): CaraWorkspaceAmsContextInput[] => {
  const pins: CaraWorkspaceAmsContextInput[] = [];
  for (const attachment of attachments) {
    if (
      (attachment.kind === 'ams-document' || attachment.kind === 'ams-context-pin') &&
      attachment.ref
    ) {
      continue;
    }
    if (attachment.kind === 'ams-document') {
      pins.push({
        entityId: attachment.id,
        integrationId: attachment.integrationId,
        entityType: KnowledgeGraphEntityType.File
      });
      continue;
    }
    if (attachment.kind === 'ams-context-pin') {
      pins.push({
        entityId: attachment.id,
        integrationId: attachment.integrationId,
        entityType: attachment.entityType
      });
    }
  }
  return pins;
};

/** The AMS browser's picks among attachments, as the mutation's `amsRefs` input. */
export const amsRefsFromAttachments = (
  attachments: CaraAttachment[]
): CaraWorkspaceAmsRefInput[] => {
  const pins: CaraWorkspaceAmsRefInput[] = [];
  for (const attachment of attachments) {
    if (
      (attachment.kind === 'ams-document' || attachment.kind === 'ams-context-pin') &&
      attachment.ref
    ) {
      pins.push({ ref: amsRefInput(attachment.ref), displayName: attachment.displayName });
    }
  }
  return pins;
};

/**
 * Returns the ids of every attachment of the given kind, preserving order.
 * Use at the submit boundary to extract custom-form / template ids that the
 * server-side mutation expects as plain arrays.
 */
export const idsByKind = (attachments: CaraAttachment[], kind: CaraAttachmentKind): string[] =>
  attachments.filter((a) => a.kind === kind).map((a) => a.id);

/**
 * Formats a byte count for display next to a filename in a chip or inside a
 * validation error message. Uses 1 fractional digit for KB/MB/GB so sizes
 * like `2.4 MB` read at a glance without being overly precise.
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
};

/**
 * Fallback when `mailboxComposeLimits.attachmentBytes` has not arrived: a
 * failed load, or a server that has not yet grown the field, must still refuse
 * a file that can never be sent rather than going quiet. Matches
 * `mailbox.InternalAttachmentLimit`.
 */
export const ATTACHMENT_LIMIT_BYTES = 32 * 1024 * 1024;

/**
 * Positive published ceilings win. Missing, zero, or negative values are the
 * fallback, so the picker keeps today's refusal while the query is in flight.
 */
export const resolveAttachmentLimit = (limitBytes?: number | null): number =>
  typeof limitBytes === 'number' && limitBytes > 0 ? limitBytes : ATTACHMENT_LIMIT_BYTES;

/**
 * Why this file cannot join the email, or undefined when it can. A file is
 * refused for the company it keeps as well as for its own weight: the ceiling
 * covers the attachments together, so two that each fit can still overflow it.
 *
 * `limitBytes` is `mailboxComposeLimits.attachmentBytes` when the composer has
 * it. Callers that omit it, or pass null, refuse at `ATTACHMENT_LIMIT_BYTES`.
 *
 * Workspace documents are absent from `attachedBytes` because they are
 * rendered at send time and have no size until then. That makes this a floor
 * rather than the real total — the server keeps the last word, and this only
 * spares the sender the refusals that were obvious from the start.
 */
export const attachmentRefusal = (candidate: {
  name: string;
  size: number;
  attachedBytes: number;
  limitBytes?: number | null;
}): string | undefined => {
  const limitBytes = resolveAttachmentLimit(candidate.limitBytes);
  if (candidate.size === 0) {
    return `“${candidate.name}” is empty, so there is nothing to attach.`;
  }
  if (candidate.size > limitBytes) {
    return `“${candidate.name}” is ${formatFileSize(candidate.size)}, and one email cannot carry more than ${formatFileSize(limitBytes)} of attachments.`;
  }
  if (candidate.attachedBytes + candidate.size > limitBytes) {
    return `Attaching “${candidate.name}” would put this email over the ${formatFileSize(limitBytes)} attachment limit. Remove something else first.`;
  }
  return undefined;
};

/**
 * Why this email body cannot be saved or sent, or undefined when it can.
 * Pasted screenshots live in the body as base64 `data:` URIs, so a couple of
 * them turn a draft into tens of megabytes of HTML that the server parses and
 * then refuses — refusing here spares the round trip and tells the sender
 * while the paste is still the last thing they did.
 *
 * `limitBytes` comes from the server (`mailboxComposeLimits.bodyBytes`) rather
 * than from a constant here, because the ceiling is derived from ceilings the
 * runtime owns. A caller without one yet refuses nothing.
 *
 * The size measured is the string's length, not the UTF-8 bytes the server
 * counts. Everything that makes a body large enough to matter — base64
 * payloads and markup — is ASCII, where the two agree, and the prose that
 * could pull them apart is orders of magnitude smaller than the slack in the
 * ceiling.
 */
export const bodyRefusal = (body: {
  size: number;
  limitBytes: number | null;
}): string | undefined => {
  if (body.limitBytes === null || body.size <= body.limitBytes) {
    return undefined;
  }
  return `This email's body is ${formatFileSize(body.size)}, over the ${formatFileSize(body.limitBytes)} limit. Remove or shrink the pasted images.`;
};

/**
 * Splits a filename into `base` (everything before the final dot) and `ext`
 * (the dot and everything after). Used by the chip to apply truncation only
 * to the base, so long filenames render as `Acme \u2026 Policy.pdf` rather
 * than losing the extension entirely.
 *
 * Returns `ext: ''` for filenames without a recognizable extension. The
 * extension cutoff (8 chars including the dot) keeps absurdly long
 * "extensions" — e.g. `Acme.Insurance.Coverage.Endorsement` — from being
 * mistaken for a real one.
 */
export const splitFileNameExtension = (name: string): { base: string; ext: string } => {
  const dot = name.lastIndexOf('.');
  if (dot <= 0 || dot >= name.length - 1) return { base: name, ext: '' };
  if (name.length - dot > 8) return { base: name, ext: '' };
  return { base: name.slice(0, dot), ext: name.slice(dot) };
};

/**
 * Best-effort unique id for a file attachment. Uses `crypto.randomUUID` when
 * available and falls back to a timestamp+random string so the hook works in
 * older browsers and in tests.
 */
const generateFileAttachmentId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `file-${crypto.randomUUID()}`;
  }
  return `file-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

export const fileAttachment = (file: File): CaraFileAttachment => ({
  kind: 'file',
  id: generateFileAttachmentId(),
  file
});

/** Constructs an attachment from a picker selection in `AmsDocumentPicker`. */
export const amsDocumentAttachment = (
  selection: AmsDocumentSelection
): CaraAmsDocumentAttachment => ({
  kind: 'ams-document',
  id: selection.id,
  integrationId: selection.integrationId,
  integrationType: selection.integrationType,
  displayName: selection.displayName,
  accountId: selection.accountId,
  accountLabel: selection.accountLabel,
  ref: selection.ref
});

export const amsContextPinAttachment = (
  selection: AmsContextPinSelection
): CaraAmsContextPinAttachment => ({
  kind: 'ams-context-pin',
  id: selection.id,
  entityType: selection.entityType,
  integrationId: selection.integrationId,
  integrationType: selection.integrationType,
  displayName: selection.displayName,
  accountId: selection.accountId,
  accountLabel: selection.accountLabel,
  ref: selection.ref
});

export type AmsPickerInitialSelection = {
  initialSelectedFiles: AmsDocumentSelection[];
  initialContextPins: AmsContextPinSelection[];
};

/** Maps compose-bar AMS attachments back into picker `initial*` props. */
export const pickerInitialSelectionFromAttachments = (
  attachments: CaraAttachment[]
): AmsPickerInitialSelection => {
  const initialSelectedFiles: AmsDocumentSelection[] = [];
  const initialContextPins: AmsContextPinSelection[] = [];

  for (const attachment of attachments) {
    if (attachment.kind === 'ams-document') {
      initialSelectedFiles.push({
        id: attachment.id,
        integrationId: attachment.integrationId,
        integrationType: attachment.integrationType,
        displayName: attachment.displayName,
        accountId: attachment.accountId,
        accountLabel: attachment.accountLabel,
        ref: attachment.ref
      });
      continue;
    }
    if (attachment.kind === 'ams-context-pin') {
      initialContextPins.push({
        id: attachment.id,
        entityType: attachment.entityType,
        integrationId: attachment.integrationId,
        integrationType: attachment.integrationType,
        displayName: attachment.displayName,
        accountId: attachment.accountId,
        accountLabel: attachment.accountLabel,
        ref: attachment.ref
      });
    }
  }

  return { initialSelectedFiles, initialContextPins };
};

/** Compose attachments from a picker confirm, dropping any prior AMS picks. */
export const replaceAmsAttachmentsFromPickerConfirm = (
  attachments: CaraAttachment[],
  result: AmsPickerConfirmResult
): CaraAttachment[] => {
  const kept = attachments.filter(
    (attachment) => attachment.kind !== 'ams-document' && attachment.kind !== 'ams-context-pin'
  );
  return [...kept, ...attachmentsFromPickerConfirm(result)];
};

export const attachmentsFromPickerConfirm = (result: AmsPickerConfirmResult): CaraAttachment[] => [
  ...result.files.map(amsDocumentAttachment),
  ...result.contextPins.map(amsContextPinAttachment)
];

/** Maps a picker confirm result to the create/message mutation's `amsContext` input. */
export const amsContextFromPickerConfirm = (
  result: AmsPickerConfirmResult
): CaraWorkspaceAmsContextInput[] =>
  amsContextFromAttachments(attachmentsFromPickerConfirm(result));

/** Maps a picker confirm result to the create/message mutation's `amsRefs` input. */
export const amsRefsFromPickerConfirm = (
  result: AmsPickerConfirmResult
): CaraWorkspaceAmsRefInput[] => amsRefsFromAttachments(attachmentsFromPickerConfirm(result));

/**
 * Merges `incoming` into `prev`, keyed by attachment id. Later entries win
 * (re-picking the same AMS entity replaces the prior chip). Local file uploads
 * keep unique generated ids, so dropping two copies of the same filename still
 * produces two chips.
 */
export const mergeAttachmentsById = (
  prev: CaraAttachment[],
  incoming: CaraAttachment[]
): CaraAttachment[] => {
  if (incoming.length === 0) return prev;

  const dedupedIncoming = new Map<string, CaraAttachment>();
  for (const attachment of incoming) {
    dedupedIncoming.set(attachment.id, attachment);
  }
  const nextIncoming = [...dedupedIncoming.values()];
  const incomingIds = new Set(nextIncoming.map((attachment) => attachment.id));

  return [...prev.filter((attachment) => !incomingIds.has(attachment.id)), ...nextIncoming];
};

/**
 * Constructs a non-file attachment from a library option. The library id is
 * reused as the chip's React key — library options are inherently unique,
 * and reusing the id makes it trivial to dedupe selections.
 */
export const templateAttachment = (
  kind: CaraTemplateAttachmentKind,
  option: { id: string; title: string }
):
  | CaraCustomFormAttachment
  | CaraWordTemplateAttachment
  | CaraExcelTemplateAttachment
  | CaraHtmlTemplateAttachment => ({
  kind,
  id: option.id,
  title: option.title
});
